<template>
    <div class="auth container-fluid">
        <div class="row justify-content-center">
            <div class="col-xl-7 col-lg-8 col-md-10 col-sm-12">
                <div class="locale_block activation_page">
                    <locale-button class="locale_block__button--first" language="nl"/>
                    <locale-button class="locale_block__button" language="fr"/>
                </div>
                <div class="block--auth has-no-top-margin">
                    <img class="logo" src="/static/img/logo_minimax.png" alt="Minimax Client Dashboard" title="Minimax Client Dashboard" height="32px">
                    <p>{{ $t('auth.header_sub_activate_account') }}</p>
                    <form class="form" @submit.prevent="activate">
                        <h1>{{ $t('auth.header_activate_account') }}</h1>
                        <span class="floating-label">{{$t('auth.desired_password')}}</span>
                        <div class="form__group">
                            <i class="form__input__icon mdi mdi-textbox-password"></i>
                            <input class="form__input--text underline has-icon"
                                   type="password"
                                   autocomplete="password"
                                   placeholder="****"
                                   v-model="credentials.password"
                            />
                        </div>
                        <span class="floating-label">{{$t('auth.repeat_desired_password')}}</span>
                        <div class="form__group">
                            <i class="form__input__icon mdi mdi-textbox-password"></i>
                            <input class="form__input--text underline has-icon"
                                   type="password"
                                   autocomplete="password"
                                   placeholder="****"
                                   v-model="credentials.passwordRepeat"
                            >
                        </div>
                        <div class="form__buttons">
                            <button type="submit" class="button--cta"
                                    :disabled="!formValidated"
                                    :class="{'is-loading': loading}">
                                {{$t('auth.button_activate_account')}}
                            </button>
                            <!-- Appears when token invalid/expired -->
                            <p v-if="!loading && invalidToken" class="activation-warning">
                                {{ $t('auth.button_reset_activate_account_link') }}
                            </p>
                            <!-- Appears when account is already activated -->
                            <p v-if="!loading && alreadyActivated" class="activation-warning clickable" @click="goToLogin">
                                {{ $t('auth.already_activated_account') }}
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import { Component, Watch } from 'vue-property-decorator';
    import {ACTIVATE} from '../../../store/modules/auth';
    import LocaleButton from '../../general/LocaleButton';
    import axios from '@/api/instance';

    @Component({
        components: { LocaleButton}
    })
    export default class ActivationForm extends Vue {
        name = 'activation-form';
        loading = false; // loading spinner
        error = '';
        formValidated = false;
        invalidToken = false;
        alreadyActivated = false;

        credentials = {
            password: '',
            passwordRepeat: ''
        };

        resendCodeStatus = {
            loading: false,
            error: '',
            hasResendCode: false
        }

        autoResent = false;

        goToLogin() {
            this.$router.push({ name: 'sign-in' }, () => {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            });
        }

        @Watch('credentials', {deep: true})
        validate(){
            if (this.credentials.password === this.credentials.passwordRepeat){
                if (this.credentials.password.length > 6) {
                    this.formValidated = true;
                }
            } else {
                this.formValidated = false;
            }
        }
        /**
         * Activate an account
         */
        async activate() {
            this.loading = true;
            var token = this.$route.params.token;

            // prepare data to send to backend
            let payload = {};
            payload.token = token;
            payload.password = this.credentials.password;
            payload.repeat_password = this.credentials.passwordRepeat;

            try {
                // try to activate account with given token and passwords
                await this.$store.dispatch(ACTIVATE, payload);
                this.$notify.success(this.$t('auth.notify_success_activate_account'));
                // redirect to account page after success
                this.$router.replace({
                    name: 'account'
                });

            } catch (e){
                // handle activation errors from backend
                if (e.response && e.response.data){

                    // get response data
                    let data = e.response.data;

                    // store backend error message
                    this.error = data.errors;

                    // reset flags for each attempt
                    this.invalidToken = false;
                    this.alreadyActivated = false;

                    switch (this.error){

                    // If link has expired
                    case 'Invalid token': {
                        // mark token as invalid → show reset hint on UI
                        this.invalidToken = true;

                        // prevent duplicate resend attempts within the same submit cycle
                        if (this.autoResent) break;

                        // get email from backend response
                        const email = data && data.email ? data.email : '';

                        if (!email) {
                            // backend couldn't determine which account this token belongs to
                            // (e.g. token row was deleted). User can't recover from here without help.
                            this.$notify.error(this.$t('auth.notify_failures.activate_account.resend_activation_link'));
                            break;
                        }

                        try {
                            // show resend loading state
                            this.resendCodeStatus.loading = true;

                            // call API to resend activation link
                            const resendResponse = await axios.post('auth/api/accounts/resend_activation_link', {
                                email,
                                platform: 'client_dashboard'
                            });

                            // backend may return 200 with success:false — treat as failure
                            if (resendResponse && resendResponse.data && resendResponse.data.success === false) {
                                throw new Error(resendResponse.data.message || 'Resend failed');
                            }

                            // mark that we already resent once so a duplicate submit doesn't spam
                            this.autoResent = true;

                            // tell the user a new link is on the way (the inline hint already says so,
                            // but a toast confirms the resend actually happened)
                            this.$notify.success(this.$t('auth.notify_success_resend_activation_link'));

                        } catch (err) {
                            // show resend failure message and let the user retry on next submit
                            this.$notify.error(this.$t('auth.notify_failures.activate_account.resend_activation_link'));
                            console.log('[activate] Auto-resend failed:', err);

                        } finally {
                            // stop resend loading spinner
                            this.resendCodeStatus.loading = false;
                        }

                        break;
                    }

                    // If account was already activated
                    case 'Already activated': {
                        this.alreadyActivated = true;
                        this.invalidToken = false;
                        break;
                    }

                    default:
                        // handle any other activation errors
                        this.$notify.error(this.$t('auth.notify_failures.activate_account.general'));
                    }
                } else {
                    // network or unknown error → show inline hint and error message
                    this.invalidToken = true;
                    this.$notify.error(this.$t('auth.notify_failures.activate_account.resend_activation_link'));
                }
            } finally {
                this.loading = false;

                // Scroll down on small screens
                if ((this.invalidToken || this.alreadyActivated) && window.innerWidth <= 768) {
                    this.$nextTick(() => {
                        const el = this.$el.querySelector('.activation-warning');
                        if (!el) return;

                        const rect = el.getBoundingClientRect();
                        const pageTop = rect.top + window.scrollY;
                        const offset = 250;

                        window.scrollTo({
                            top: pageTop - offset,
                            behavior: 'smooth'
                        });
                    });
                }
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/auth/forms/ActivationForm.vue